import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { signIn, resetPassword } from '@/services/auth';
import { getIdTokenResult } from 'firebase/auth';
import { getUserRole, getUserCouncilorId, getUserInstitutionId } from '@/services/firestore';
import { db } from '@/firebase';
import { doc, setDoc } from 'firebase/firestore';

export default function LoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [userType, setUserType] = useState<'admin' | 'vereador' | 'instituicao' | 'gestor'>('vereador');
  const emailRef = useRef<HTMLInputElement>(null);
  const emailAdminRef = useRef<HTMLInputElement>(null);
  const emailEntityRef = useRef<HTMLInputElement>(null);
  const [showPwdV, setShowPwdV] = useState(false);
  const [showPwdA, setShowPwdA] = useState(false);
  const [showPwdI, setShowPwdI] = useState(false);
  const adminBase = import.meta.env.DEV ? '/admin-api' : ((import.meta.env.VITE_ADMIN_SERVER_URL as string) || '');

  // Agora usamos Firebase Auth; as credenciais de demonstração foram removidas.

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    let email = formData.get('email') as string;
    const password = formData.get('password') as string;
    try {
      if (userType === 'instituicao') {
        const rawCnpj = (formData.get('cnpj') as string) || '';
        const digits = rawCnpj.replace(/\D/g, '');
        if (digits.length !== 14) throw new Error('CNPJ inválido');
        email = `cnpj-${digits}@instituicao.local`;
      }
      const cred = await signIn(email, password);
      const user = cred.user;
      // Tenta ler role e councilorId no Firestore; em caso de permissão negada, faz fallback para custom claims
      let role: 'admin' | 'vereador' | 'instituicao' | 'gestor' | undefined;
      let councilorId: string | undefined;
      let institutionId: string | undefined;
      try {
        role = await getUserRole(user.uid);
      } catch (e) {
        console.warn('Falha ao ler role no Firestore, usando custom claims.', e);
      }
      try {
        councilorId = await getUserCouncilorId(user.uid);
      } catch (e) {
        console.warn('Falha ao ler councilorId no Firestore, usando custom claims.', e);
      }
      if (!role) {
        try {
          const token = await getIdTokenResult(user);
          role = token.claims?.role as 'admin' | 'vereador' | 'instituicao' | 'gestor' | undefined;
        } catch { }
      }
      if (!councilorId) {
        try {
          const token = await getIdTokenResult(user);
          councilorId = token.claims?.councilorId as string | undefined;
        } catch { }
      }
      try {
        institutionId = await getUserInstitutionId(user.uid);
      } catch { }

      // Se ainda não há vínculo, tenta vincular automaticamente via Admin Server por email
      if (!role || (role === 'vereador' && !councilorId) || (role === 'instituicao' && !institutionId) || (userType === 'instituicao' && !institutionId)) {
        try {
          const { adminHealth, adminPost } = await import('@/services/admin-api');
          const ok = await adminHealth();
          if (!ok) {
            console.warn('Servidor admin indisponível para auto-vínculo.');
            if (!role && userType === 'instituicao') {
              try {
                role = 'instituicao';
                const emailStr = user.email || '';
                const match = emailStr.match(/cnpj-(\d{14})@instituicao\.local/);
                const digits = match ? match[1] : undefined;
                let institutionId: string | undefined;
                if (digits) {
                  try {
                    const { listInstitutions } = await import('@/services/firestore');
                    const insts = await listInstitutions();
                    const found = insts.find(i => (i.cnpj || '').replace(/\D/g, '') === digits);
                    institutionId = found?.id;
                  } catch { }
                }
                await setDoc(doc(db, 'users', user.uid), { role: 'instituicao', institutionId }, { merge: true });
              } catch { }
            }
          } else {
            const desiredRole = role || (userType === 'instituicao' ? 'instituicao' : 'vereador');
            const resp = await adminPost('/link-user', { email: user.email, role: desiredRole });
            if (resp.ok) {
              // Força atualização do token para carregar novos custom claims
              await user.getIdToken(true);
              const token = await getIdTokenResult(user);
              role = token.claims?.role as 'admin' | 'vereador' | 'instituicao' | 'gestor' | undefined;
              councilorId = token.claims?.councilorId as string | undefined;
              institutionId = (token.claims as any)?.institutionId as string | undefined;
            } else {
              const data = await resp.json().catch(() => ({}));
              console.warn('Falha ao vincular automaticamente:', data?.error || resp.statusText);
            }
          }
        } catch (linkErr) {
          console.warn('Erro ao chamar admin auto-link:', linkErr);
        }
      }

      if ((userType === 'instituicao' && !institutionId) || (role === 'instituicao' && !institutionId)) {
        try {
          const emailStr = user.email || '';
          const match = emailStr.match(/cnpj-(\d{14})@instituicao\.local/);
          const digits = match ? match[1] : undefined;
          let targetInstId: string | undefined;
          if (digits) {
            const { listInstitutions } = await import('@/services/firestore');
            const insts = await listInstitutions();
            const found = insts.find(i => (i.cnpj || '').replace(/\D/g, '') === digits);
            targetInstId = found?.id;
          }
          await setDoc(doc(db, 'users', user.uid), { role: 'instituicao', institutionId: targetInstId || null, forcePasswordChange: true }, { merge: true });
          const idToken = await user.getIdToken();
          if (idToken) {
            const resp = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
            if (resp.ok) {
              await user.getIdToken(true);
              const tk = await getIdTokenResult(user);
              role = tk.claims?.role as 'admin' | 'vereador' | 'instituicao' | 'gestor' | undefined;
              institutionId = (tk.claims as any)?.institutionId as string | undefined;
            }
          }
        } catch { }
      }
      if (!role && userType === 'admin') {
        try {
          await setDoc(doc(db, 'users', user.uid), { role: 'admin' }, { merge: true });
          console.info('Auth mapping reconciled for admin', { uid: user.uid, email: user.email });
          try {
            const idToken = await user.getIdToken();
            const resp = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
            if (resp.ok) {
              await user.getIdToken(true);
              const tk = await getIdTokenResult(user);
              role = tk.claims?.role as 'admin' | 'vereador' | 'instituicao' | 'gestor' | undefined;
            }
          } catch { }
        } catch (e) {
          console.warn('Failed to reconcile admin role mapping', e);
        }
      }
      if (!role && userType === 'vereador') {
        try {
          const emailStr = user.email || '';
          const { listCouncilors } = await import('@/services/firestore');
          const list = await listCouncilors();
          const found = list.find(c => (c.email || '').toLowerCase() === emailStr.toLowerCase());
          if (found) {
            await setDoc(doc(db, 'users', user.uid), { role: 'vereador', councilorId: found.id }, { merge: true });
            console.info('Auth mapping reconciled for councilor', { uid: user.uid, email: emailStr, councilorId: found.id });
            try {
              const idToken = await user.getIdToken();
              const resp = await fetch(`${adminBase}/ensure-claims`, { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
              if (resp.ok) {
                await user.getIdToken(true);
                const tk = await getIdTokenResult(user);
                role = tk.claims?.role as 'admin' | 'vereador' | 'instituicao' | 'gestor' | undefined;
                councilorId = tk.claims?.councilorId as string | undefined;
              }
            } catch { }
          }
        } catch (e) {
          console.warn('Failed to reconcile councilor role mapping', e);
        }
      }
      const userName = user.displayName || user.email || 'Usuário';
      // Legado: manter localStorage para páginas existentes até migração completa
      localStorage.setItem('userType', (role || userType));
      localStorage.setItem('userName', userName);

      toast({
        title: 'Login realizado com sucesso',
        description: `Bem-vindo, ${userName}!`,
      });

      // Regra: instituição deve trocar senha no próximo login
      if (role === 'instituicao') {
        try {
          const { getUserForcePasswordChange } = await import('@/services/firestore');
          const mustChange = await getUserForcePasswordChange(user.uid);
          if (mustChange) {
            localStorage.setItem('forcePasswordChange', 'true');
            toast({ title: 'Segurança', description: 'Por favor, altere sua senha antes de continuar.' });
            navigate('/instituicao?tab=seguranca');
            return;
          }
        } catch { }
      }
      if (role === 'admin') {
        navigate('/admin');
      } else if (role === 'vereador') {
        navigate('/vereador');
      } else if (role === 'instituicao') {
        navigate('/instituicao');
      } else if (role === 'gestor') {
        navigate('/gestor');
      } else {
        if (userType === 'instituicao') navigate('/instituicao'); else navigate('/admin');
      }
    } catch (err) {
      const code = (err as any)?.code as string | undefined;
      const msg =
        code === 'auth/operation-not-allowed'
          ? 'Modo de login por email/senha está desativado no Firebase.'
          : code === 'auth/user-not-found'
            ? 'Usuário não encontrado.'
            : code === 'auth/wrong-password'
              ? 'Senha incorreta.'
              : code === 'auth/too-many-requests'
                ? 'Muitas tentativas. Tente novamente mais tarde.'
                : 'Verifique email e senha ou contate o suporte.';
      console.error('Login error:', code, err);
      toast({
        title: 'Erro no login',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    let email: string | undefined;
    if (userType === 'admin') email = emailAdminRef.current?.value;
    else if (userType === 'vereador') email = emailRef.current?.value;
    else {
      const el = document.getElementById('cnpj-entity') as HTMLInputElement | null;
      const digits = (el?.value || '').replace(/\D/g, '');
      if (digits.length === 14) email = `cnpj-${digits}@instituicao.local`;
    }
    if (!email) {
      toast({
        title: 'Informe seu email',
        description: 'Digite seu email para recuperar a senha.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await resetPassword(email);
      toast({
        title: 'Email enviado',
        description: 'Verifique sua caixa de entrada para redefinir a senha.',
      });
    } catch (err) {
      toast({
        title: 'Erro ao enviar email',
        description: 'Confirme o email e tente novamente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-accent/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4">
            <img src="/favicon.ico" alt="Logo do Sistema" className="h-12 w-12" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Sistema de Emendas
          </h1>
          <p className="text-muted-foreground">
            Portal de Gestão de Emendas Individuais
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Acesso ao Sistema</CardTitle>
            <CardDescription>
              Selecione o tipo de acesso e entre com suas credenciais
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="vereador" onValueChange={(value) => setUserType(value as 'admin' | 'vereador' | 'instituicao' | 'gestor')}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="vereador">
                  <i className="fas fa-user mr-2"></i>
                  Vereador
                </TabsTrigger>
                <TabsTrigger value="admin">
                  <i className="fas fa-user-shield mr-2"></i>
                  Admin
                </TabsTrigger>
                <TabsTrigger value="gestor">
                  <i className="fas fa-user-tie mr-2"></i>
                  Gestor
                </TabsTrigger>
                <TabsTrigger value="instituicao">
                  <i className="fas fa-building mr-2"></i>
                  Instituição
                </TabsTrigger>
              </TabsList>

              <TabsContent value="vereador">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="p-4 bg-primary/5 rounded-lg mb-4">
                    <p className="text-sm text-muted-foreground">
                      <i className="fas fa-info-circle mr-2"></i>
                      Acesso para vereadores gerenciarem suas emendas
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Digite seu email"
                      required
                      disabled={isLoading}
                      ref={emailRef}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Senha</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPwdV ? 'text' : 'password'}
                        placeholder="Digite sua senha"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        aria-label={showPwdV ? 'Ocultar senha' : 'Mostrar senha'}
                        onMouseDown={() => setShowPwdV(true)}
                        onMouseUp={() => setShowPwdV(false)}
                        onMouseLeave={() => setShowPwdV(false)}
                        onTouchStart={() => setShowPwdV(true)}
                        onTouchEnd={() => setShowPwdV(false)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <i className={`fas ${showPwdV ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Entrando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt mr-2"></i>
                        Entrar como Vereador
                      </>
                    )}
                  </Button>

                </form>
              </TabsContent>

              <TabsContent value="admin">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="p-4 bg-destructive/5 rounded-lg mb-4">
                    <p className="text-sm text-muted-foreground">
                      <i className="fas fa-shield-alt mr-2"></i>
                      Acesso administrativo para gerenciar todo o sistema
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="email-admin">Email Administrativo</Label>
                    <Input
                      id="email-admin"
                      name="email"
                      type="email"
                      placeholder="Digite seu email admin"
                      required
                      disabled={isLoading}
                      ref={emailAdminRef}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password-admin">Senha Administrativa</Label>
                    <div className="relative">
                      <Input
                        id="password-admin"
                        name="password"
                        type={showPwdA ? 'text' : 'password'}
                        placeholder="Digite a senha admin"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        aria-label={showPwdA ? 'Ocultar senha' : 'Mostrar senha'}
                        onMouseDown={() => setShowPwdA(true)}
                        onMouseUp={() => setShowPwdA(false)}
                        onMouseLeave={() => setShowPwdA(false)}
                        onTouchStart={() => setShowPwdA(true)}
                        onTouchEnd={() => setShowPwdA(false)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <i className={`fas ${showPwdA ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" variant="destructive" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Entrando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-user-shield mr-2"></i>
                        Entrar como Administrador
                      </>
                    )}
                  </Button>

                </form>
              </TabsContent>
              <TabsContent value="instituicao">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="p-4 rounded-lg mb-4 border border-emerald-200 bg-emerald-50 text-emerald-900">
                    <p className="text-sm text-muted-foreground">
                      <i className="fas fa-info-circle mr-2"></i>
                      Acesso para instituições receptoras das emendas
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="cnpj-entity">CNPJ da Instituição</Label>
                    <Input
                      id="cnpj-entity"
                      name="cnpj"
                      type="text"
                      placeholder="00.000.000/0000-00"
                      required
                      disabled={isLoading}
                      onChange={(e) => {
                        const v = e.currentTarget.value.replace(/\D/g, '').slice(0, 14);
                        const p = [v.slice(0, 2), v.slice(2, 5), v.slice(5, 8), v.slice(8, 12), v.slice(12, 14)];
                        let out = '';
                        if (p[0]) out = p[0];
                        if (p[1]) out += '.' + p[1];
                        if (p[2]) out += '.' + p[2];
                        if (p[3]) out += '/' + p[3];
                        if (p[4]) out += '-' + p[4];
                        e.currentTarget.value = out;
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password-entity">Senha</Label>
                    <div className="relative">
                      <Input
                        id="password-entity"
                        name="password"
                        type={showPwdI ? 'text' : 'password'}
                        placeholder="Digite sua senha"
                        required
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        aria-label={showPwdI ? 'Ocultar senha' : 'Mostrar senha'}
                        onMouseDown={() => setShowPwdI(true)}
                        onMouseUp={() => setShowPwdI(false)}
                        onMouseLeave={() => setShowPwdI(false)}
                        onTouchStart={() => setShowPwdI(true)}
                        onTouchEnd={() => setShowPwdI(false)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <i className={`fas ${showPwdI ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Entrando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-building mr-2"></i>
                        Entrar como Instituição
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="gestor">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="p-4 bg-purple-50 text-purple-900 rounded-lg mb-4 border border-purple-200">
                    <p className="text-sm">
                      <i className="fas fa-tasks mr-2"></i>
                      Acesso para gestores analisarem emendas
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="email-gestor">Email Gestor</Label>
                    <Input id="email-gestor" name="email" type="email" placeholder="gestor@sinop.mt.leg.br" required disabled={isLoading} />
                  </div>
                  <div>
                    <Label htmlFor="password-gestor">Senha</Label>
                    <Input id="password-gestor" name="password" type="password" placeholder="Sua senha" required disabled={isLoading} />
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Entrando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt mr-2"></i>
                        Entrar como Gestor
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center">
              <Dialog>
                <DialogTrigger asChild>
                  <Button type="button" variant="link" className="p-0">Recuperar senha</Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>Ajuda para redefinir senha</DialogTitle>
                    <DialogDescription>Para redefinição de senha, entre em contato com o setor de desenvolvimento</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                    <div className="sm:col-span-1 flex justify-center">
                      <img src="/WhatsApp Image 2025-02-17 at 19.08.53.jpeg" alt="Perfil de contato" className="w-28 h-28 rounded-full object-cover shadow" />
                    </div>
                    <div className="sm:col-span-2 space-y-3">
                      <a href="https://wa.me/5566996701140?text=Ol%C3%A1!%20Gostaria%20de%20redefinir%20minha%20senha." target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-full px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                        <i className="fab fa-whatsapp mr-2"></i>
                        WhatsApp 66 99670-1140
                      </a>
                      <a href="mailto:manager@sinop.mt.leg.br?subject=Redefini%C3%A7%C3%A3o%20de%20senha&body=Ol%C3%A1,%20preciso%20redefinir%20minha%20senha." className="inline-flex items-center justify-center w-full px-3 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90">
                        <i className="fas fa-envelope mr-2"></i>
                        manager@sinop.mt.leg.br
                      </a>
                      <div className="text-sm text-muted-foreground">
                        Se preferir, informe seu email ou CNPJ abaixo e enviaremos um link de redefinição.
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input id="modal-email" placeholder="Seu email" aria-label="Email para redefinição" />
                        <Input id="modal-cnpj" placeholder="CNPJ da instituição" aria-label="CNPJ da instituição" />
                      </div>
                      <div className="flex justify-end">
                        <Button type="button" variant="outline" onClick={() => {
                          const emailEl = document.getElementById('modal-email') as HTMLInputElement | null;
                          const cnpjEl = document.getElementById('modal-cnpj') as HTMLInputElement | null;
                          const email = (emailEl?.value || '').trim();
                          const raw = (cnpjEl?.value || '').replace(/\D/g, '');
                          const instEmail = raw.length === 14 ? `cnpj-${raw}@instituicao.local` : undefined;
                          const target = email || instEmail;
                          if (!target) {
                            toast({ title: 'Informe email ou CNPJ', description: 'Preencha um dos campos para enviar o link.', variant: 'destructive' });
                            return;
                          }
                          resetPassword(target).then(() => {
                            toast({ title: 'Email enviado', description: 'Verifique sua caixa de entrada para redefinir a senha.' });
                          }).catch(() => {
                            toast({ title: 'Erro ao enviar email', description: 'Confirme os dados e tente novamente.', variant: 'destructive' });
                          });
                        }}>Enviar link de redefinição</Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} DOMPR Câmara Municipal de Sinop - MT
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Todos os direitos reservados
          </p>
        </div>
      </div>
    </div>
  );
}
